import { useCallback, useLayoutEffect, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import type { HomeSnapshot, JourneyBrief, JourneyLeg, JourneyMapSnapshot, LatLng, TravelActivityKind } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import {
  currentLeg,
  effectiveDeparture,
  legRoute,
  legTitle,
  modeLabel,
  vehicleAnchorAt,
} from "@/shared/journey/vehicle";
import { currentLegText } from "@/shared/journey/currentLegText";
import { SchematicMapSurface } from "@/shared/map";
import { queryKeys } from "@/shared/query/queryClient";
import { useJourneyReader } from "@/shared/query/journeyReader";
import { useServices } from "@/shared/services/registry";
import { useActiveHome } from "@/shared/session/householdContext";
import { Slot } from "@/shared/slots/Slot";
import type { JourneyOverlayProps } from "@/shared/slots/names";
import { useNow } from "@/shared/time/clock";
import {
  Button,
  DataOriginBadge,
  ErrorState,
  Icon,
  LoadingState,
  Page,
  PetAvatar,
  TopBar,
  ToggleChip,
} from "@/shared/ui";
import { SourceLink, safeSceneHref, useSceneReturnedHref } from "@/shared/ui/sceneNavigation";
import { resetFixtureEpoch } from "@/fixtures/world";
import { useVisitSetting } from "@/features/venue/useVisitSetting";
import type { VisitSetting } from "@/features/venue/visitKind";
import { JourneyAtHome } from "./DepartureStation";
import { tripTitleLine } from "./tripTitle";
import { arrivalRemaining, departureLabel, departureMs, estimateLeftText } from "./factTime";
import { JourneyPlayerFacts } from "./JourneyPlayerFacts";
import { useJourneyHistory } from "./journeyHistoryReader";
import "./journey.css";

const ACTIVITY_TEXT: Record<TravelActivityKind, string> = {
  listening: "在听歌",
  watching: "在看片",
  resting: "在歇一会儿",
  window_gazing: "在看窗外",
  dining: "在吃点东西",
  writing_postcard: "在写明信片",
};

const MOVING: ReadonlyArray<JourneyLeg["phase"]> = ["connecting", "boarding", "in_transit", "arriving"];

function clean(name: string): string {
  return name.replace("（示意）", "");
}

/** Display facts use the explicit server current ID; map geometry keeps its existing helper. */
function factLeg(snapshot: JourneyMapSnapshot): JourneyLeg | undefined {
  return snapshot.legs.find((leg) => leg.leg_id === snapshot.current_leg_id);
}

function movingLeg(snapshot: JourneyMapSnapshot): JourneyLeg | undefined {
  const leg = factLeg(snapshot);
  return !snapshot.current_visit_id && leg && MOVING.includes(leg.phase) ? leg : undefined;
}

/**
 * 旅途状态：只从服务端给的行程事实推导，不替 TA 编理由。
 * visitSetting：这次到访是什么地方（见 venue/visitKind，按到访的场景模板判断，不按名字猜）。
 * 只有确定是门店（cafe / restaurant）才说“在店里”；小路、公园、打工的地方，以及还没读到、读不到时，都说“到了”，不写“店”字。
 */
export function tripState(snapshot: JourneyMapSnapshot, nowMs: number, visitSetting: VisitSetting | null = null) {
  const leg = factLeg(snapshot);
  const first = snapshot.legs[0];
  const departure = first ? departureMs(first.times) : NaN;
  const day = Number.isFinite(departure) && Number.isFinite(nowMs) ? Math.max(1, Math.floor((nowMs - departure) / 86_400_000) + 1) : null;
  const visiting = Boolean(snapshot.current_visit_id);
  const atShop = visiting && visitSetting === "shop";
  const moving = Boolean(movingLeg(snapshot));
  const index = leg ? snapshot.legs.findIndex((item) => item.leg_id === leg.leg_id) : -1;
  const next = leg?.phase === "arrived" ? snapshot.legs[index + 1] : leg;
  const place = visiting ? snapshot.destination_title ?? (leg ? clean(leg.destination.name) : null) : leg ? clean(moving ? leg.destination.name : leg.phase === "arrived" ? leg.destination.name : leg.origin.name) : null;
  const state = visiting ? (atShop ? "在店里" : "到了") : moving ? "在路上" : leg?.phase === "waiting" || leg?.phase === "scheduled" ? "等出发" : leg?.phase === "arrived" ? "停留" : leg?.phase === "cancelled" ? "路段已取消" : "状态待更新";
  return { leg, day, visiting, atShop, moving, next, place, state };
}

/** Compatibility export: the route card states estimates without promising arrival. */
export function legLeftText(ms: number): string {
  return estimateLeftText(ms);
}

/** Accept the headline only for this journey/version, visit and server moving leg. */
export function briefIsCurrent(brief: JourneyBrief | null | undefined, snapshot: JourneyMapSnapshot, _nowMs: number): boolean {
  if (!brief || brief.journey_id !== snapshot.journey_id || brief.itinerary_version !== snapshot.itinerary_version) return false;
  const walking = movingLeg(snapshot);
  return (brief.current_visit_id ?? null) === (snapshot.current_visit_id ?? null) && (brief.current_leg_id ?? null) === (walking?.leg_id ?? null);
}

/**
 * 此刻能用的那句话：对得上就是家园快照里那句；对不上是 null（状态签不写第三行，状态卡用不带它的说法），
 * 并请家园快照重读一次。同一刻（同一趟、同一次到访、同一段）只请一次：后端要是真的还没跟上，也不会来回刷。
 */
function useCurrentHeadline(home: HomeSnapshot | undefined, snapshot: JourneyMapSnapshot, nowMs: number): string | null {
  const queryClient = useQueryClient();
  const brief = home?.journey ?? null;
  const fits = briefIsCurrent(brief, snapshot, nowMs);
  const walking = movingLeg(snapshot);
  const moment = `${snapshot.journey_id}|${snapshot.itinerary_version}|${snapshot.current_visit_id ?? "-"}|${walking?.leg_id ?? "-"}`;
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!home || fits || asked.current === moment) return;
    asked.current = moment;
    void queryClient.invalidateQueries({ queryKey: queryKeys.home });
  }, [home, fits, moment, queryClient]);
  return fits && brief ? brief.headline : null;
}

function TripStatusChip({ home, headline, snapshot, nowMs, visitSetting }: { home: HomeSnapshot | undefined; headline: string | null; snapshot: JourneyMapSnapshot; nowMs: number; visitSetting: VisitSetting | null }) {
  const { day, place, state } = tripState(snapshot, nowMs, visitSetting);
  const pet = home?.pet;
  return (
    <div className="ps-trip-chip" data-testid="trip-chip">
      {/* 状态签是固定纸色（journey.css）：头像按纸面写，深色下爪印、插画的底块不变成纸上的深色块 */}
      {pet ? <PetAvatar petId={pet.pet_id} name={pet.name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} surface="paper" size={40} /> : null}
      <div>
        <strong>{pet?.name ?? "TA"}{day === null ? "" : ` · 第 ${day} 天`}</strong>
        <span>{place ? `${place} · ${state}` : state}</span>
        {headline ? <small>{headline}</small> : null}
      </div>
    </div>
  );
}

function TripStatusCard({ brief, snapshot, nowMs, visitSetting }: { brief: string | null; snapshot: JourneyMapSnapshot; nowMs: number; visitSetting: VisitSetting | null }) {
  const [open, setOpen] = useState(false);
  const returnedFrom=useSceneReturnedHref();
  useLayoutEffect(()=>{if(returnedFrom && returnedFrom===safeSceneHref(`/guides?journey=${encodeURIComponent(snapshot.journey_id)}`))setOpen(true);},[returnedFrom,snapshot.journey_id]);
  const { leg, moving, next, visiting, atShop, state } = tripState(snapshot, nowMs, visitSetting);
  const activity = snapshot.activities.find((item) => item.state === "active");
  // 家园快照没有这趟的说法、或那句话说的不是此刻（见 briefIsCurrent）时，用旅程标题；标题本身就是“去对岸看看”这样的短语，不再拼“去”（见 ./tripTitle）
  const headline = brief ?? tripTitleLine("这趟", snapshot.destination_title) ?? "TA 在旅途中";
  // 交通方式写“下一站”那一段的（2026-09-25，巡检 R10-06）：到了、换乘间隙时 current_leg 是刚走完的那段（服务端规定 TA 人还在那儿），
  // 写它的方式会拼成“步行 · 下一站 家”，而下一段其实是打车。在路上、等出发时下一站就是这一段，写法照旧；没有下一站时仍写这一段的。
  const ride = next ?? leg;
  const meta = [
    // 交通方式认不出（缺 mode、契约新加的方式）时这一段不写（REL-02 3.4，原来写出“undefined”）
    ride ? (ride.world_service ? legTitle(ride) : modeLabel(ride.mode)) : null,
    next ? `下一站 ${clean(next.destination.name)}` : null,
    moving && leg ? legLeftText(arrivalRemaining(leg.times, nowMs)) : null,
  ].filter(Boolean);
  return (
    <section className={`ps-trip-card${open ? " is-open" : ""}`} aria-label="TA 此刻的旅途">
      <button type="button" className="ps-trip-card__head" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span className="ps-trip-card__icon" aria-hidden="true"><Icon name={visiting ? (atShop ? "cup" : "pin") : moving ? (leg?.mode === "walk" ? "compass" : "journey") : "pin"} size={20} /></span>
        <span className="ps-trip-card__text">
          <span className="ps-trip-card__eyebrow"><i aria-hidden="true" />TA {state}{activity && !headline.includes(ACTIVITY_TEXT[activity.kind].slice(1)) ? ` · ${ACTIVITY_TEXT[activity.kind]}` : ""}</span>
          <strong>{headline}</strong>
          {/* 几段之间“·”前面是不断行空格：“·”粘在上一段末尾，窄屏不会跑到行首（320 宽原来是“……东京羽田 / · 还要 3 小时”）；
              配合 journey.css 的 keep-all 只在普通空格处折行，“小时”“分钟”和站名不再被拆开（390 宽原来是“还要 3 小 / 时”）。 */}
          {meta.length ? <small>{meta.join("\u00a0· ")}</small> : null}
        </span>
        <Icon name="chevron" size={18} className="ps-trip-card__chevron" />
      </button>
      {open ? (
        <div className="ps-trip-card__body">
          <h3>这一趟的路线</h3>
          <ol className="ps-trip-legs">
            {snapshot.legs.map((item) => {
              const done = item.phase === "arrived";
              const current = item.leg_id === leg?.leg_id && !done && item.phase !== "cancelled";
              const mode = item.world_service ? legTitle(item) : modeLabel(item.mode);
              return (
                <li key={item.leg_id} className={done ? "is-done" : current ? "is-current" : ""}>
                  <span className="ps-trip-legs__time" title={item.times.origin_timezone} style={{ width: 92, overflowWrap: "anywhere", whiteSpace: "pre-line" }}>{departureLabel(item.times).replace(" · ", "\n")}</span>
                  <span className="ps-trip-legs__what">
                    <strong>{clean(item.origin.name)} → {clean(item.destination.name)}</strong>
                    {/* 正在这一段上按交通方式说（和地图“这趟旅途”面板共用 shared/journey/currentLegText）：原来写死“正在走”，坐出租车也这么写 */}
                    {/* 交通方式认不出时只写“走过了 / 在路上 / 接下来”，前面不留“·”（REL-02 3.4） */}
                    <small>{mode ? `${mode} · ` : null}{done ? "走过了" : item.phase === "cancelled" ? "已取消" : current ? (visiting ? "停留" : MOVING.includes(item.phase) ? currentLegText(item.mode) : item.phase === "scheduled" || item.phase === "waiting" ? "等出发" : "状态待更新") : "接下来"}</small>
                  </span>
                </li>
              );
            })}
          </ol>
          {/* 四个入口的字只在 <wbr> 处折行（journey.css 的 .ps-journey-links a span：keep-all）：按词断，不把最后一个字挤下去
              （原来 390 宽“列车照片与结 / 果”、360 宽“TA 的旅行手 / 账”、320 宽“带回的收 / 藏”）。<wbr> 不进读屏和文字内容。 */}
          <nav className="ps-journey-links" aria-label="这趟旅程的记录">
            <SourceLink to={`/guides?journey=${encodeURIComponent(snapshot.journey_id)}`}><Icon name="bookmark" size={17} /><span>TA 的<wbr />旅行<wbr />手账</span></SourceLink>
            <Link to="/communicator?channel=family"><Icon name="mail" size={17} /><span>旅途<wbr />来信</span></Link>
            <Link to="/collection"><Icon name="gift" size={17} /><span>带回的<wbr />收藏</span></Link>
            <Link to="/album"><Icon name="album" size={17} /><span>历史<wbr />相册</span></Link>
          </nav>
          {/* 底下的提示两句各占一行（看行程卡、一起听一起看是两件事）：每句不超过 19 个字，280–430 宽都放得下，句子中间不折行。
              原来一句连着写，320 宽折成“旁边的音符或电视可 / 以一起听看。”，“一起听看”也拗口（2026-09-25 改说法）。 */}
          <p className="ps-trip-card__foot">点地图上的交通工具看行程卡。<br />点旁边的音符或电视就能一起听、一起看。</p>
        </div>
      ) : null}
    </section>
  );
}

function JourneyMap({
  snapshot,
  nowMs,
  home,
}: {
  snapshot: JourneyMapSnapshot;
  nowMs: number;
  home: HomeSnapshot | undefined;
}) {
  const [params, setParams] = useSearchParams();
  const sheet = params.get("sheet");
  const [sheetKind, sheetId] = sheet
    ? (sheet.split(":", 2) as ["leg" | "media", string])
    : [null, null];

  const openSheet = useCallback(
    (kind: "leg" | "media", id: string) => {
      const next = new URLSearchParams(params);
      next.set("sheet", `${kind}:${id}`);
      setParams(next);
    },
    [params, setParams],
  );
  const closeSheet = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("sheet");
    setParams(next);
  }, [params, setParams]);

  const leg = currentLeg(snapshot);
  const anchor = vehicleAnchorAt(snapshot, nowMs);
  const bounds = useMemo<LatLng[]>(
    () => snapshot.legs.flatMap((l) => legRoute(l)),
    [snapshot.legs],
  );
  const routes = useMemo(
    () =>
      snapshot.legs.map((l) => ({
        id: l.leg_id,
        points: legRoute(l),
        dashed: l.position_basis === "schematic",
        active: l.leg_id === leg?.leg_id,
      })),
    [snapshot.legs, leg?.leg_id],
  );
  // 当前站：这一段还没出发就是起点（TA 还在那儿），否则是终点（TA 在的地方，或正在去的地方）。
  // 两站挨得太近时留它的站名，另一站缩成小点（见 shared/map/placeLabels；巡检 2026-09-27：家和附近的咖啡馆站名叠在一起）。
  const here = leg ? `${leg.leg_id}-${nowMs < effectiveDeparture(leg.times) ? "o" : "d"}` : null;
  const places = useMemo(
    () =>
      snapshot.legs.flatMap((l) => [
        {
          id: `${l.leg_id}-o`,
          at: { lat: l.origin.lat ?? 0, lng: l.origin.lng ?? 0 },
          name: clean(l.origin.name),
          primary: `${l.leg_id}-o` === here,
        },
        {
          id: `${l.leg_id}-d`,
          at: { lat: l.destination.lat ?? 0, lng: l.destination.lng ?? 0 },
          name: clean(l.destination.name),
          primary: `${l.leg_id}-d` === here,
        },
      ]),
    [snapshot.legs, here],
  );
  const overlayProps: JourneyOverlayProps = { snapshot, nowMs, openSheet };
  // 在不在店里要看这次到访的场景模板（快照里没有），和到访页共用一份到访数据
  const visitSetting = useVisitSetting(snapshot.current_visit_id);
  // 家园快照那句话只在说的是此刻时才用（状态签第三行、状态卡标题同一个判断）
  const headline = useCurrentHeadline(home, snapshot, nowMs);

  return (
    <>
      <div className="ps-trip-stage">
        <div className="ps-journey-map" data-testid="journey-map">
          <SchematicMapSurface
            bounds={bounds}
            routes={routes}
            places={places}
            focus={anchor?.point ?? null}
            label={leg?.position_basis === "schematic" ? "线路示意" : "示意地图"}
          >
            <Slot name="journey.map.overlay" props={overlayProps} />
          </SchematicMapSurface>
          <TripStatusChip home={home} headline={headline} snapshot={snapshot} nowMs={nowMs} visitSetting={visitSetting} />
        </div>
        {snapshot.catching_up ? <p className="ps-journey-catching-up" role="status">TA 的位置继续按时间移动；刚到站的小事还在补记，来信与结算稍后会出现。</p> : null}
        <JourneyPlayerFacts snapshot={snapshot} home={home} />
        <TripStatusCard brief={headline} snapshot={snapshot} nowMs={nowMs} visitSetting={visitSetting} />
      </div>
      <div className="ps-row" style={{ marginTop: 8 }}>
        <DataOriginBadge
          origin={snapshot.data_origin}
          label="演示行程：不对应真实班次或车辆位置"
        />
      </div>
      <div className="ps-stack" style={{ marginTop: 12 }}>
        <Slot name="journey.cards" props={overlayProps} />
      </div>
      <Slot name="journey.dock" props={overlayProps} />
      {sheetKind && sheetId ? (
        <Slot
          name="journey.sheet"
          props={{
            snapshot,
            nowMs,
            kind: sheetKind,
            targetId: sheetId,
            close: closeSheet,
          }}
        />
      ) : null}
    </>
  );
}



export function JourneyPage() {
  const { transport } = useServices();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const scenario = params.get("scenario") ?? undefined;
  const nowMs = useNow(1000);
  const home = useActiveHome();
  const reader = useJourneyReader({ home, scenario });
  const recent = useJourneyHistory(home);
  const map = reader.query;
  const atHome = env.dataMode === "live" && !home.isError && home.data?.presence === "at_home";
  const scenarios = transport.fixtureScenarios();
  const showHome = atHome || (env.dataMode === "live" && reader.details.status === "none");
  // Only the still-authorized exact home observer may refresh on a server-completed journey.
  const lifecycle = reader.details.status !== "failed" ? map.data?.lifecycle : null;
  useEffect(() => {
    if (lifecycle !== "completed" || reader.scope?.mode !== "live") return;
    const key = queryKeys.homeFor(reader.scope.userId, reader.scope.petId);
    const state = queryClient.getQueryState(key);
    if (state?.status === "success" && state.fetchFailureCount === 0) void queryClient.invalidateQueries({ queryKey: key, exact: true });
  }, [lifecycle, reader.fingerprint, queryClient]);

  return (
    <Page className={`ps-journey-page${showHome ? "" : " is-traveling"}`}>
      {showHome ? (
        <TopBar title={home.data ? `${home.data.pet.name}的旅途` : "旅途"} subtitle="出不出门由 TA 决定" sourceBack={{ fallback:"/map", fallbackLabel:"返回地图" }} />
      ) : (
        <>
          {/* 旅途中这页同样是不显示底栏的二级页（方案 2.2）：左上角也要能回地图首页，否则是死胡同。
              顶栏标题本身就是这一页的 h1（UI-BASE-01b），原来另放的那个只给读屏的 h1 不要了。 */}
          <TopBar title={home.data ? `${home.data.pet.name}的旅途` : "旅途"} sourceBack={{ fallback:"/map", fallbackLabel:"返回地图" }} />
        </>
      )}
      {env.dataMode === "fixture" && scenarios.length ? (
        <details className="ps-scenario-disclosure">
          <summary>切换测试场景</summary>
          <div className="ps-scenarios" role="group" aria-label="演示场景">
            {scenarios.map((s) => (
              <ToggleChip
                key={s.id}
                pressed={(scenario ?? "flight") === s.id}
                onToggle={() => {
                  const next = new URLSearchParams();
                  next.set("scenario", s.id);
                  setParams(next);
                }}
              >
                {s.label}
              </ToggleChip>
            ))}
            <Button
              size="sm"
              variant="ghost"
              icon="refresh"
              onClick={() => {
                resetFixtureEpoch();
                void queryClient.invalidateQueries({ queryKey: reader.key, exact: true });
                void queryClient.invalidateQueries({
                  queryKey: ["companion-media"],
                });
              }}
            >
              重置演示时间
            </Button>
          </div>
        </details>
      ) : null}
      {home.isError ? (
        <ErrorState error={home.error} onRetry={() => void home.refetch()} />
      ) : showHome && home.data ? (
        <>
        <JourneyAtHome home={home.data} last={atHome ? (recent.state === "ready" ? recent.snapshot : null) : map.data ?? null} />
          {atHome ? (
            recent.state === "ready" ? <JourneyPlayerFacts snapshot={recent.snapshot} home={home.data} /> : recent.state === "inactive" ? null : (
              <section className="ps-trip-facts" aria-label="最近一趟旅程">
                {recent.state === "loading" ? <LoadingState lines={1} label="正在翻看最近一趟旅程…" /> : recent.state === "error" ? <ErrorState error={recent.error} onRetry={recent.retry} /> : <p className="ps-trip-facts__unknown">{recent.state === "empty" ? "还没有旅行记录" : "上一趟的住宿记录暂未确认"}</p>}
              </section>
            )
          ) : reader.details.status !== "failed" && (map.data?.lifecycle === "completed" || map.data?.lifecycle === "cancelled") ? <JourneyPlayerFacts snapshot={map.data} home={home.data} /> : null}
        </>
      ) : reader.details.status === "failed" ? (
        <ErrorState error={reader.details.error} onRetry={reader.details.refresh ?? undefined} />
      ) : reader.details.status === "aligned" ? (
        <JourneyMap snapshot={reader.details.snapshot} nowMs={nowMs} home={home.data} />
      ) : (
        <LoadingState lines={2} label="正在寻找 TA 的位置…" />
      )}
    </Page>
  );
}
